import type {
    LinhaImportacao, OrganizacaoImportacao, PessoaImportacao,
} from '../services/ImportacaoService';

/* ─────────────────────────────────────────────────────────────
   Leitura de planilha e sugestão de colunas para a importação de
   contatos e organizações (04/10/2026).

   O "sistema entende sozinho" é só a SUGESTÃO: pelo cabeçalho e,
   quando ele não diz nada, pelo conteúdo. Quem decide é a pessoa,
   na tela de colunas, antes de qualquer coisa ser gravada. Medido
   na planilha que motivou a feature (export de outro CRM, 1.204
   linhas): a coluna `tenant_name` tem o mesmo valor em todas as
   linhas — é a conta do cliente no sistema antigo, não a empresa
   de cada contato. Por isso coluna constante não vira identidade.
   ───────────────────────────────────────────────────────────── */

export interface Planilha {
    arquivo: string;
    cabecalho: string[];
    /** Só as linhas de dados, já sem as totalmente vazias, todas com a largura do cabeçalho. */
    linhas: string[][];
    /** Número de cada linha no arquivo (o cabeçalho é a 1), para o aviso apontar onde olhar. */
    numeros: number[];
}

export type CampoPessoa = keyof PessoaImportacao;
export type CampoOrganizacao = keyof OrganizacaoImportacao;
export type Destino = '' | `pessoa.${CampoPessoa}` | `organizacao.${CampoOrganizacao}`;
export type Contexto = 'contatos' | 'organizacoes';

/* O rótulo é o que o seletor mostra fechado: campo que existe nos dois lados diz de quem é,
   senão "Telefone" na tela de Organizações não conta se é do contato ou da empresa. */
export const DESTINOS: { value: Exclude<Destino, ''>; label: string; grupo: 'Contato' | 'Organização' }[] = [
    { value: 'pessoa.nome', label: 'Nome do contato', grupo: 'Contato' },
    { value: 'pessoa.telefone', label: 'Telefone do contato', grupo: 'Contato' },
    { value: 'pessoa.email', label: 'E-mail do contato', grupo: 'Contato' },
    { value: 'pessoa.segmento', label: 'Segmento do contato', grupo: 'Contato' },
    { value: 'pessoa.cep', label: 'CEP do contato', grupo: 'Contato' },
    { value: 'pessoa.logradouro', label: 'Endereço do contato', grupo: 'Contato' },
    { value: 'pessoa.numero', label: 'Nº do endereço do contato', grupo: 'Contato' },
    { value: 'pessoa.complemento', label: 'Complemento do contato', grupo: 'Contato' },
    { value: 'pessoa.bairro', label: 'Bairro do contato', grupo: 'Contato' },
    { value: 'pessoa.cidade', label: 'Cidade do contato', grupo: 'Contato' },
    { value: 'pessoa.estado', label: 'Estado do contato', grupo: 'Contato' },
    { value: 'organizacao.cnpj', label: 'CNPJ', grupo: 'Organização' },
    { value: 'organizacao.razaoSocial', label: 'Razão social', grupo: 'Organização' },
    { value: 'organizacao.nomeFantasia', label: 'Nome fantasia', grupo: 'Organização' },
    { value: 'organizacao.site', label: 'Site', grupo: 'Organização' },
    { value: 'organizacao.email', label: 'E-mail da organização', grupo: 'Organização' },
    { value: 'organizacao.telefone', label: 'Telefone da organização', grupo: 'Organização' },
    { value: 'organizacao.segmento', label: 'Segmento da organização', grupo: 'Organização' },
    { value: 'organizacao.cep', label: 'CEP da organização', grupo: 'Organização' },
    { value: 'organizacao.logradouro', label: 'Endereço da organização', grupo: 'Organização' },
    { value: 'organizacao.numero', label: 'Nº do endereço da organização', grupo: 'Organização' },
    { value: 'organizacao.complemento', label: 'Complemento da organização', grupo: 'Organização' },
    { value: 'organizacao.bairro', label: 'Bairro da organização', grupo: 'Organização' },
    { value: 'organizacao.cidade', label: 'Cidade da organização', grupo: 'Organização' },
    { value: 'organizacao.estado', label: 'Estado da organização', grupo: 'Organização' },
];

export const rotuloDestino = (d: Destino) => DESTINOS.find(x => x.value === d)?.label ?? 'Ignorar';

/* ─── Leitura ─────────────────────────────────────────────── */

export async function lerPlanilha(arquivo: File): Promise<Planilha> {
    const extensao = arquivo.name.toLowerCase().split('.').pop() ?? '';

    let celulas: string[][];
    if (extensao === 'xlsx') {
        // Só carrega a biblioteca de Excel quando alguém importa um .xlsx.
        const { readSheet } = await import('read-excel-file/browser');
        const dados = await readSheet(arquivo);
        celulas = dados.map(linha => linha.map(textoDaCelula));
    } else if (extensao === 'csv' || extensao === 'txt') {
        celulas = lerCsv(decodificar(await arquivo.arrayBuffer()));
    } else if (extensao === 'xls') {
        throw new Error('Arquivo .xls (Excel antigo) não é aceito. Abra no Excel e salve como .xlsx ou .csv.');
    } else {
        throw new Error('Formato não aceito. Use um arquivo .csv ou .xlsx.');
    }

    const numerados = celulas
        .map((linha, i) => ({ linha: linha.map(c => c.trim()), numero: i + 1 }))
        .filter(l => l.linha.some(c => c !== ''));

    if (numerados.length === 0) throw new Error('A planilha está vazia.');

    const [cabecalhoBruto, ...dados] = numerados;
    const largura = Math.max(...numerados.map(l => l.linha.length));
    const cabecalho = Array.from({ length: largura }, (_, i) => cabecalhoBruto.linha[i] || `Coluna ${i + 1}`);

    if (dados.length === 0) throw new Error('A planilha só tem o cabeçalho, nenhuma linha de dados.');

    return {
        arquivo: arquivo.name,
        cabecalho,
        linhas: dados.map(l => Array.from({ length: largura }, (_, i) => l.linha[i] ?? '')),
        numeros: dados.map(l => l.numero),
    };
}

function textoDaCelula(valor: unknown): string {
    if (valor === null || valor === undefined) return '';
    if (valor instanceof Date) return valor.toLocaleDateString('pt-BR');
    if (typeof valor === 'boolean') return valor ? 'Sim' : 'Não';
    return String(valor);
}

/**
 * UTF-8 primeiro; se o arquivo não for UTF-8 válido, é o Windows-1252 que o Excel brasileiro
 * grava ao "Salvar como CSV" — sem isso, "Imóveis" chegaria como "Im�veis".
 */
function decodificar(buffer: ArrayBuffer): string {
    let texto: string;
    try {
        texto = new TextDecoder('utf-8', { fatal: true }).decode(buffer);
    } catch {
        texto = new TextDecoder('windows-1252').decode(buffer);
    }
    return texto.replace(/^﻿/, '');
}

/** CSV com aspas, quebra de linha dentro de aspas e separador detectado (vírgula, ponto e vírgula ou tab). */
function lerCsv(texto: string): string[][] {
    const separador = detectarSeparador(texto);
    const linhas: string[][] = [];
    let linha: string[] = [];
    let campo = '';
    let entreAspas = false;

    for (let i = 0; i < texto.length; i++) {
        const c = texto[i];
        if (entreAspas) {
            if (c === '"') {
                if (texto[i + 1] === '"') { campo += '"'; i++; }
                else entreAspas = false;
            } else {
                campo += c;
            }
        } else if (c === '"') {
            entreAspas = true;
        } else if (c === separador) {
            linha.push(campo); campo = '';
        } else if (c === '\n' || c === '\r') {
            if (c === '\r' && texto[i + 1] === '\n') i++;
            linha.push(campo); campo = '';
            linhas.push(linha); linha = [];
        } else {
            campo += c;
        }
    }
    if (campo !== '' || linha.length > 0) { linha.push(campo); linhas.push(linha); }
    return linhas;
}

function detectarSeparador(texto: string): string {
    const contagem: Record<string, number> = { ',': 0, ';': 0, '\t': 0 };
    let entreAspas = false;
    for (const c of texto) {
        if (c === '"') entreAspas = !entreAspas;
        else if (!entreAspas && (c === '\n' || c === '\r')) break;
        else if (!entreAspas && c in contagem) contagem[c]++;
    }
    const [maisFrequente, vezes] = Object.entries(contagem).sort((a, b) => b[1] - a[1])[0];
    return vezes > 0 ? maisFrequente : ',';
}

/* ─── Sugestão de colunas ─────────────────────────────────── */

export interface Sugestao {
    destino: Destino;
    /** Por que a sugestão é essa, quando não é óbvio pelo nome da coluna. */
    nota?: string;
    /** A nota pede atenção (coluna repetida, campo já tomado), não só explica. */
    alerta?: boolean;
}

type Compartilhado = 'email' | 'telefone' | 'segmento' | 'cep' | 'logradouro' | 'numero' | 'complemento' | 'bairro' | 'cidade' | 'estado';

interface Regra {
    termos: string[];
    /** Campo que existe nos dois lados: vai para o lado da tela de onde a importação abriu. */
    campo?: Compartilhado;
    destino?: Destino;
    ignorar?: string;
}

/* A ordem importa: identificador e data antes de "nome", senão "owner_name" vira nome; e os
   campos que existem nos dois lados antes de "empresa", senão "E-mail da empresa" vira razão social. */
const REGRAS: Regra[] = [
    { termos: ['id', 'uuid', 'guid', 'codigo', 'cod'], ignorar: 'Identificador do sistema antigo.' },
    { termos: ['owner', 'responsavel', 'vendedor', 'dono', 'proprietario', 'assigned', 'usuario', 'user'], ignorar: 'O contato não tem responsável no Brik.' },
    { termos: ['created', 'updated', 'criado', 'criacao', 'atualizado', 'atualizacao', 'data', 'date', 'dt', 'modified'], ignorar: 'Datas do sistema antigo não são importadas.' },
    { termos: ['source', 'origem', 'fonte', 'canal', 'utm'], ignorar: 'O Brik não guarda a origem do cadastro antigo.' },
    { termos: ['tenant', 'workspace'], ignorar: 'Conta no sistema antigo, não um dado do contato.' },
    { termos: ['cnpj'], destino: 'organizacao.cnpj' },
    { termos: ['cpf', 'rg'], ignorar: 'O Brik não guarda CPF nem RG.' },
    { termos: ['fantasia'], destino: 'organizacao.nomeFantasia' },
    { termos: ['site', 'website', 'url', 'homepage'], destino: 'organizacao.site' },
    { termos: ['email', 'mail', 'emails'], campo: 'email' },
    { termos: ['telefone', 'telefones', 'phone', 'phones', 'celular', 'whatsapp', 'fone', 'mobile', 'tel', 'cel', 'wpp', 'zap'], campo: 'telefone' },
    { termos: ['segmento', 'segment', 'setor', 'ramo', 'industry', 'categoria'], campo: 'segmento' },
    { termos: ['cep', 'zip', 'postal'], campo: 'cep' },
    { termos: ['logradouro', 'endereco', 'rua', 'street', 'address', 'avenida'], campo: 'logradouro' },
    { termos: ['numero', 'number', 'num', 'nro'], campo: 'numero' },
    { termos: ['complemento', 'complement'], campo: 'complemento' },
    { termos: ['bairro', 'neighborhood', 'district'], campo: 'bairro' },
    { termos: ['cidade', 'city', 'municipio'], campo: 'cidade' },
    { termos: ['estado', 'uf', 'state'], campo: 'estado' },
    { termos: ['razao', 'empresa', 'company', 'organizacao', 'organization', 'companhia'], destino: 'organizacao.razaoSocial' },
    { termos: ['nome', 'name', 'cliente', 'lead', 'pessoa', 'person', 'contato', 'contact'], destino: 'pessoa.nome' },
];

/** Palavras que puxam um campo dos dois lados para a organização: "Telefone da empresa". */
const DA_ORGANIZACAO = ['empresa', 'company', 'organizacao', 'organization', 'companhia', 'razao'];

/** Destinos que identificam alguém: coluna com o mesmo valor em todas as linhas não pode ser um deles. */
const IDENTIDADE: Destino[] = ['pessoa.nome', 'pessoa.telefone', 'pessoa.email', 'organizacao.razaoSocial', 'organizacao.nomeFantasia'];

const normalizar = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ').trim();

/* camelCase e snake_case viram palavras: "personName" e "person_name" → ["person", "name"]. */
const palavras = (cabecalho: string) => normalizar(cabecalho.replace(/([a-z])([A-Z])/g, '$1 $2')).split(' ').filter(Boolean);

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function porConteudo(valores: string[], lado: 'pessoa' | 'organizacao'): Sugestao | null {
    if (valores.length === 0) return null;
    const fracao = (teste: (v: string) => boolean) => valores.filter(teste).length / valores.length;

    if (fracao(v => UUID.test(v)) >= 0.8) return { destino: '', nota: 'Identificador do sistema antigo.' };
    if (fracao(v => EMAIL.test(v)) >= 0.7) return { destino: `${lado}.email`, nota: 'Reconhecida pelo conteúdo.' };
    if (fracao(v => v.replace(/[^0-9A-Za-z]/g, '').length === 14 && /\d{2}$/.test(v)) >= 0.7) {
        return { destino: 'organizacao.cnpj', nota: 'Reconhecida pelo conteúdo.' };
    }
    if (fracao(v => /^[\d\s()+\-.]+$/.test(v) && v.replace(/\D/g, '').length >= 10 && v.replace(/\D/g, '').length <= 13) >= 0.7) {
        return { destino: `${lado}.telefone`, nota: 'Reconhecida pelo conteúdo.' };
    }
    return null;
}

export function sugerirColunas(planilha: Planilha, contexto: Contexto): Sugestao[] {
    const lado = contexto === 'contatos' ? 'pessoa' : 'organizacao';
    const usados = new Map<Destino, string>();

    return planilha.cabecalho.map((titulo, coluna) => {
        const termos = palavras(titulo);
        const valores = planilha.linhas.map(l => l[coluna]).filter(v => v !== '').slice(0, 200);

        let sugestao: Sugestao = { destino: '' };
        const regra = REGRAS.find(r => r.termos.some(t => termos.includes(t)));
        if (regra?.ignorar) {
            sugestao = { destino: '', nota: regra.ignorar };
        } else if (regra?.campo) {
            const doLado = termos.some(t => DA_ORGANIZACAO.includes(t)) ? 'organizacao' : lado;
            sugestao = { destino: `${doLado}.${regra.campo}` };
        } else if (regra?.destino) {
            // "Nome" numa planilha aberta em Organizações é o nome da empresa.
            sugestao = { destino: regra.destino === 'pessoa.nome' && contexto === 'organizacoes' ? 'organizacao.razaoSocial' : regra.destino };
        } else {
            sugestao = porConteudo(valores, lado) ?? { destino: '' };
        }

        const distintos = new Set(planilha.linhas.map(l => l[coluna].toLowerCase()).filter(v => v !== ''));
        // Coluna que o nome já mandou ignorar ("Responsável", "Data de cadastro") fica com o motivo dela.
        if (!regra?.ignorar && planilha.linhas.length >= 3 && distintos.size === 1 && valores.length >= 2) {
            const valor = valores[0];
            if (sugestao.destino === 'organizacao.cnpj') {
                sugestao = { ...sugestao, nota: 'O mesmo CNPJ em todas as linhas: todos vão para a mesma organização.', alerta: true };
            } else if (sugestao.destino === '' || IDENTIDADE.includes(sugestao.destino)) {
                sugestao = { destino: '', nota: `"${valor}" em todas as linhas — parece a conta no sistema antigo, não um dado de cada contato.`, alerta: true };
            } else {
                sugestao = { ...sugestao, nota: 'O mesmo valor em todas as linhas.', alerta: true };
            }
        }

        if (sugestao.destino !== '') {
            const dono = usados.get(sugestao.destino);
            if (dono !== undefined) {
                return { destino: '', nota: `Também parece "${rotuloDestino(sugestao.destino)}", que ficou com a coluna "${dono}".`, alerta: true };
            }
            usados.set(sugestao.destino, titulo);
        }
        return sugestao;
    });
}

/** Pode seguir quando há como identificar alguém: telefone ou e-mail do contato, ou CNPJ. */
export const temIdentidade = (destinos: Destino[]) =>
    destinos.some(d => d === 'pessoa.telefone' || d === 'pessoa.email' || d === 'organizacao.cnpj');

export function montarLinhas(planilha: Planilha, destinos: Destino[]): LinhaImportacao[] {
    return planilha.linhas.map((celulas, i) => {
        const linha: LinhaImportacao = { linha: planilha.numeros[i] };
        destinos.forEach((destino, coluna) => {
            const valor = celulas[coluna];
            if (!destino || !valor) return;
            const [lado, campo] = destino.split('.') as ['pessoa' | 'organizacao', string];
            const alvo = (linha[lado] ??= {}) as Record<string, string>;
            alvo[campo] = valor;
        });
        return linha;
    });
}
